import React, { createRef, useRef } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import DatePicker from "sassy-datepicker";
import moment from "moment-timezone";
import { TimeSelection } from "../components";
import { Button } from "@mui/material";
import * as Images from "../themes/images";
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "100%",
  bgcolor: "background.paper",
  border: "2px solid #001571",
  boxShadow: 24,
  p: 2,
  maxWidth: "900px",
  maxHeight: "90%",
  borderRadius: "6px",
};

class BookingTimeModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      selectedTime: null,
      selectedDate: null,
      onLoadDate: null,
      locationAvailability: null,
      step: 1,
      removeClass: false,
      interval: 15,
    };
    this.handleStep = this.handleStep.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleOpen = this.handleOpen.bind(this);
    this.onDateChange = this.onDateChange.bind(this);
    this.selectTime = this.selectTime.bind(this);
    this.mobileDateRef = createRef();
    this.desktopDateRef = createRef();
  }

  handleOpen() {
    this.setState({
      selectedDate: moment().format("MMM Do"),
    });
    this.props.toggleDateModal(true);
  }

  handleStep(val) {
    this.setState({
      step: this.state.step + val,
    });
  }
  handleClose() {
    this.props.toggleDateModal(false);
    this.setState({
      locationAvailability: null,
      step: 1,
    });
    this.props.resetAvailability(); // this function is not working
  }
  // Helper function to check if a date matches a closure rule
  matchesClosureRule(date, rule) {
    const checkDate = moment(date);
    if (!checkDate.isValid()) {
      return false;
    }

    // Check if rule has start/end date constraints
    if (rule.startDate && checkDate.isBefore(moment(rule.startDate), "day")) {
      return false;
    }
    if (rule.endDate && checkDate.isAfter(moment(rule.endDate), "day")) {
      return false;
    }

    // Evaluate based on rule type
    switch (rule.ruleType) {
      case "firstSundayOfMonth":
        return this.isFirstDayOfWeekInMonth(checkDate, 0);
      case "lastSundayOfMonth":
        return this.isLastDayOfWeekInMonth(checkDate, 0);
      case "firstMondayOfMonth":
        return this.isFirstDayOfWeekInMonth(checkDate, 1);
      case "lastMondayOfMonth":
        return this.isLastDayOfWeekInMonth(checkDate, 1);
      case "firstTuesdayOfMonth":
        return this.isFirstDayOfWeekInMonth(checkDate, 2);
      case "lastTuesdayOfMonth":
        return this.isLastDayOfWeekInMonth(checkDate, 2);
      case "firstWednesdayOfMonth":
        return this.isFirstDayOfWeekInMonth(checkDate, 3);
      case "lastWednesdayOfMonth":
        return this.isLastDayOfWeekInMonth(checkDate, 3);
      case "firstThursdayOfMonth":
        return this.isFirstDayOfWeekInMonth(checkDate, 4);
      case "lastThursdayOfMonth":
        return this.isLastDayOfWeekInMonth(checkDate, 4);
      case "firstFridayOfMonth":
        return this.isFirstDayOfWeekInMonth(checkDate, 5);
      case "lastFridayOfMonth":
        return this.isLastDayOfWeekInMonth(checkDate, 5);
      case "firstSaturdayOfMonth":
        return this.isFirstDayOfWeekInMonth(checkDate, 6);
      case "lastSaturdayOfMonth":
        return this.isLastDayOfWeekInMonth(checkDate, 6);
      case "specificDayOfMonth":
        if (rule.dayOfMonth) {
          return checkDate.date() === rule.dayOfMonth;
        }
        return false;
      case "specificDateOfYear":
        // Check if both month and dayOfMonth are provided
        // moment months are 0-indexed (0-11), so we subtract 1 from rule.month
        if (rule.month && rule.dayOfMonth) {
          return (
            checkDate.month() === rule.month - 1 &&
            checkDate.date() === rule.dayOfMonth
          );
        }
        return false;
      case "dateRange":
        // startDate/endDate already validated above - if we reach here, date is in range
        return !!(rule.startDate && rule.endDate);
      default:
        return false;
    }
  }

  isFirstDayOfWeekInMonth(date, dayOfWeek) {
    if (date.day() !== dayOfWeek) {
      return false;
    }
    const firstOfMonth = date.clone().startOf("month");
    const firstDayOfWeek = firstOfMonth.day(dayOfWeek);
    if (firstDayOfWeek.month() !== date.month()) {
      firstDayOfWeek.add(7, "days");
    }
    return date.isSame(firstDayOfWeek, "day");
  }

  isLastDayOfWeekInMonth(date, dayOfWeek) {
    if (date.day() !== dayOfWeek) {
      return false;
    }
    const lastOfMonth = date.clone().endOf("month");
    const lastDayOfWeek = lastOfMonth.day(dayOfWeek);
    if (lastDayOfWeek.month() !== date.month()) {
      lastDayOfWeek.subtract(7, "days");
    }
    return date.isSame(lastDayOfWeek, "day");
  }

  // Check if a date is closed (including closure rules)
  isDateClosed(dateString) {
    const location = this.props.location;
    if (!location) {
      return false;
    }

    const checkDate = moment(dateString, ["YYYY-MM-DD", "dddd, MMMM Do YYYY"]);

    // Check closure rules first
    const closureRules = location.closureRules || [];
    if (checkDate.isValid()) {
      for (const rule of closureRules) {
        if (this.matchesClosureRule(checkDate, rule)) {
          return true;
        }
      }
    }

    // Check traditional closedDates
    const closedDates = location.closedDates;
    if (!closedDates) {
      return false;
    }

    if (
      closedDates[dateString] === true ||
      closedDates[dateString] === "true" ||
      closedDates[dateString] === "closed" ||
      closedDates[dateString] === 1 ||
      (typeof closedDates[dateString] !== "undefined" &&
        closedDates[dateString] !== null &&
        closedDates[dateString] !== false)
    ) {
      return true;
    }

    // Check for alternative date formats
    const dateWithoutLeadingZero = moment(dateString, [
      "YYYY-MM-DD",
      "dddd, MMMM Do YYYY",
    ]).format("YYYY-M-D");
    if (closedDates[dateWithoutLeadingZero]) {
      return true;
    }

    return false;
  }

  // Check if a date should be disabled in the DatePicker
  shouldDisableDate(date) {
    const dateString = moment(date).format("YYYY-MM-DD");
    return this.isDateClosed(dateString);
  }

  onDateChange(date) {
    let selectedDate = moment(date).format("YYYY-MM-DD");

    // Check if date is closed (including closure rules)
    if (this.isDateClosed(selectedDate)) {
      this.props.selectedDate(new Date());
      alert("Sorry, no booking times are available for this date.");
      this.setState({
        removeClass: true,
      });
      return;
    }

    this.setState({
      removeClass: false,
    });
    this.setState({
      selectedTime: null,
      selectedDate: moment(date).format("MMM Do"),
    });

    if (this.props.updateBooking) {
      this.props.updateSelectedDate(selectedDate);
    } else {
      this.props.selectedDate(selectedDate);
    }
  }

  selectTime(cap, time) {
    // let dateTime = this.props.selectedBookingTime ||  new Date()
    // dateTime = dateTime.split(" ");
    let date = this.state.selectedDate
      ? this.state.selectedDate
      : moment().format("MMM Do");
    if (cap > 0) {
      this.setState({
        selectedTime: time,
      });
      let t = time;
      let ampm = t.split(" - ")[0].includes("AM") ? "AM" : "PM";
      let selectedDateTimeUTC = moment
        .utc(date + " " + t.split(" - ")[0], "MMM Do hA")
        .format("YYYY-MM-DD HH:mm:ss");
      this.props.handleSelectedTime(
        date + " " + time,
        selectedDateTimeUTC,
        ampm,
        time
      );
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.openBookingModal !== this.props.openBookingModal) {
      if (!this.props.updateBooking) {
        this.setState({
          open: this.props.openBookingModal,
          selectedTime: this.props.selectedTime,
        });
        let dateTime = this.props.selectedBookingTime;
        let date = false;
        if (dateTime) {
          dateTime = dateTime.split(" ");
          date = moment(dateTime[0] + " " + dateTime[1], "MMM Do").format(
            "YYYY-MM-DD"
          );
          let selectedDate = moment(date).format("YYYY-MM-DD");
          this.props.selectedDate(selectedDate);
        } else {
          let selectedDate = moment().format("YYYY-MM-DD");
          this.props.selectedDate(selectedDate);
        }
      } else {
        this.setState({
          open: this.props.openBookingModal,
          selectedTime: this.props.selectedTime,
        });

        let dateTime = this.props.selectedBookingTime;
        let date = false;
        if (dateTime) {
          dateTime = dateTime.split(" ");
          date = moment(dateTime[0] + " " + dateTime[1], "MMM Do").format(
            "YYYY-MM-DD"
          );
          let selectedDate = moment(date).format("YYYY-MM-DD");
          this.props.updateSelectedDate(selectedDate);
        }
      }
    }

    if (this.state.removeClass) {
      if (this.desktopDateRef.current) {
        const children = this.desktopDateRef.current.children;

        for (let i = 0; i < children[1].children.length; i++) {
          children[1].children[i].classList.remove("sdp--date-btn__selected");
        }
      }
      if (this.mobileDateRef.current) {
        const children = this.mobileDateRef.current.children;

        for (let i = 0; i < children[1].children.length; i++) {
          children[1].children[i].classList.remove("sdp--date-btn__selected");
        }
      }
    }

    if (prevProps.locationAvailability !== this.props.locationAvailability) {
      this.setState({
        locationAvailability: this.props.locationAvailability,
      });
    }
  }
  render() {
    let dateTime = this.props.selectedBookingTime;
    let date = false;
    if (dateTime) {
      dateTime = dateTime.split(" ");
      date = moment(dateTime[0] + " " + dateTime[1], "MMM Do").format(
        "YYYY-MM-DD"
      );
      date = new Date(date);
    }
    let hideCount = 0;
    return (
      <div>
        <Modal
          open={this.state.open}
          // onClose={() =>  this.handleClose()}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          className="popupModalDateTime"
        >
          {window.innerWidth > 820 ? (
            <Box sx={style}>
              <h2 className="modalTitle">Arrival Time</h2>
              <p className="modalDesc">
                Note: Bookings within the hour aren't accepted.
              </p>
              <div className="calendarTimeSection">
                <div className="twoColumn">
                  <h4>Select your date</h4>
                  <div className="calendarArea">
                    <DatePicker
                      ref={this.desktopDateRef}
                      minDate={new Date(moment().subtract(1, "days"))}
                      className="bookingCalendar"
                      onChange={this.onDateChange}
                      selected={date ? date : new Date()}
                      shouldDisableDate={this.shouldDisableDate.bind(this)}
                    />
                  </div>
                </div>
                <div className="twoColumn">
                  <h4>Select your arrival time</h4>
                  <div className="timeArea">
                    {this.state.locationAvailability === null ? (
                      <p>Select a date to retrieve times...</p>
                    ) : (
                      (() => {
                        const selectedDate =
                          this.state.selectedDate === null
                            ? moment().format("YYYY-MM-DD")
                            : this.state.selectedDate;

                        // ✅ Early exit if selected date is fully closed (including closure rules)
                        const selectedDateFormatted = moment(
                          selectedDate,
                          "MMM Do"
                        ).format("YYYY-MM-DD");
                        if (this.isDateClosed(selectedDateFormatted)) {
                          return (
                            <p>
                              Sorry, No times available for the date selected.
                            </p>
                          );
                        }

                        let hideCount = 0;

                        const visibleTimes =
                          this.state.locationAvailability.map((key, i) => {
                            let now = moment();
                            let lastBookableTime = moment(
                              key.timeStart,
                              "HH:mm"
                            ).add(
                              this.props.availibilityInterval
                                ? this.props.availibilityInterval
                                : this.state.interval,
                              "minutes"
                            );
                            let startBookableTime = moment(
                              key.timeStart,
                              "HH:mm"
                            );

                            let checkTimeBefore = false;
                            if (this.state.selectedDate == null) {
                              checkTimeBefore =
                                moment().format("YYYY-MM-DD") ===
                                moment().format("YYYY-MM-DD");
                            } else {
                              checkTimeBefore =
                                moment(
                                  this.state.selectedDate,
                                  "MMM Do"
                                ).format("YYYY-MM-DD") ===
                                moment().format("YYYY-MM-DD");
                            }

                            if (checkTimeBefore) {
                              if (
                                lastBookableTime.isBefore(now) ||
                                now.isBetween(
                                  startBookableTime,
                                  lastBookableTime
                                )
                              ) {
                                hideCount++;
                                return null;
                              }
                            }

                            if (this.state.selectedDate !== null) {
                              let selectedDateTime = moment(
                                this.state.selectedDate,
                                "MMM Do"
                              ).format("YYYY-MM-DD");

                              const earlyCloseDate =
                                this.props.location.earlyCloseTime;
                              const openFromDate =
                                this.props.location.openFromTime;
                              const timeAllowed =
                                earlyCloseDate &&
                                earlyCloseDate[selectedDateTime]
                                  ? Number(earlyCloseDate[selectedDateTime])
                                  : 24;
                              const timeOpenFrom =
                                openFromDate && openFromDate[selectedDateTime]
                                  ? Number(openFromDate[selectedDateTime])
                                  : 0;

                              let [hoursStr, minutesStr] =
                                key.timeStart.split(":");
                              let hours = parseInt(hoursStr);
                              let minutes = parseInt(minutesStr);
                              let decimalHours = hours + minutes / 60;
                              if (
                                decimalHours >= timeAllowed ||
                                decimalHours < timeOpenFrom
                              ) {
                                hideCount++;
                                return null;
                              }
                            }

                            return (
                              <TimeSelection
                                key={i}
                                objectKey={key.timeStart}
                                time={key.capacity}
                                interval={
                                  this.props.availibilityInterval
                                    ? this.props.availibilityInterval
                                    : this.state.interval
                                }
                                displayInterval={
                                  this.props.displayInterval
                                    ? this.props.displayInterval
                                    : this.state.interval
                                }
                                selectedTime={this.state.selectedTime}
                                select={this.selectTime}
                                discount={this.props.discount}
                              />
                            );
                          });

                        if (
                          hideCount === this.state.locationAvailability.length
                        ) {
                          return (
                            <p>
                              Sorry, No times available for the date selected.
                            </p>
                          );
                        }

                        return visibleTimes;
                      })()
                    )}
                  </div>
                </div>
              </div>
              <div className="modalFooter">
                <Button
                  className="selectButton"
                  onClick={() => {
                    this.props.updateSelectedService();
                    this.props.toggleDateModal(false);
                  }}
                >
                  Select
                </Button>
              </div>
            </Box>
          ) : (
            <Box sx={style}>
              <div
                className={`dateTimeStep dateTimeStep${this.state.step} `}
              ></div>
              <div className="closeButton">
                <img
                  src={Images.closeIcon}
                  alt=""
                  onClick={() => this.handleClose()}
                />
              </div>
              <h2 className="modalTitle">Booking date & time</h2>
              <p className="modalDesc">
                Note: Bookings withing the hour aren't accepted.
              </p>
              <div className="calendarTimeSection">
                <div className="twoColumn">
                  {this.state.step === 1 ? (
                    <>
                      <h4>Select your date</h4>
                      <div className="calendarArea">
                        <DatePicker
                          ref={this.mobileDateRef}
                          minDate={new Date(moment().subtract(1, "days"))}
                          className="bookingCalendar"
                          onChange={this.onDateChange}
                          selected={date ? date : new Date()}
                          shouldDisableDate={this.shouldDisableDate.bind(this)}
                        />
                      </div>
                    </>
                  ) : (
                    <div
                      className="selectedDate"
                      onClick={() => {
                        this.handleStep(-1);
                      }}
                    >
                      <img src={Images.blueLeftArrow} alt="" />
                      <p>
                        {this.state.selectedDate
                          ? `${this.state.selectedDate} ${
                              this.state.selectedTime
                                ? this.state.selectedTime
                                : dateTime
                                ? `${dateTime[2]} - ${dateTime[4]}`
                                : ""
                            }`
                          : this.props.selectedBookingTime}
                      </p>
                    </div>
                  )}
                </div>
                <div className="twoColumn">
                  {this.state.step === 2 && (
                    <>
                      <h4 className="selectTimeTitle">
                        Select your arrival time
                      </h4>
                      <div className="timeArea">
                        {this.state.locationAvailability === null ? (
                          <p>Select a date to retrieve times...</p>
                        ) : (
                          (() => {
                            const selectedDate =
                              this.state.selectedDate === null
                                ? moment().format("YYYY-MM-DD")
                                : this.state.selectedDate;

                            // ✅ Check if selected date is fully closed (including closure rules)
                            const selectedDateFormatted = moment(
                              selectedDate,
                              "MMM Do"
                            ).format("YYYY-MM-DD");
                            if (this.isDateClosed(selectedDateFormatted)) {
                              return (
                                <p>
                                  Sorry, No times available for the date
                                  selected.
                                </p>
                              );
                            }

                            let hideCount = 0;

                            const visibleTimes =
                              this.state.locationAvailability.map((key, i) => {
                                const now = moment();
                                const lastBookableTime = moment(
                                  key.timeStart,
                                  "HH:mm"
                                ).add(
                                  this.props.availibilityInterval
                                    ? this.props.availibilityInterval
                                    : this.state.interval,
                                  "minutes"
                                );
                                const startBookableTime = moment(
                                  key.timeStart,
                                  "HH:mm"
                                );

                                let checkTimeBefore = false;

                                if (this.state.selectedDate == null) {
                                  checkTimeBefore =
                                    moment().format("YYYY-MM-DD") ===
                                    moment().format("YYYY-MM-DD");
                                } else {
                                  checkTimeBefore =
                                    moment(
                                      this.state.selectedDate,
                                      "MMM Do"
                                    ).format("YYYY-MM-DD") ===
                                    moment().format("YYYY-MM-DD");
                                }

                                if (
                                  checkTimeBefore &&
                                  lastBookableTime.isBefore(now)
                                ) {
                                  hideCount++;
                                  return null;
                                }

                                const selectedDateTime = moment(
                                  this.state.selectedDate,
                                  "MMM Do"
                                ).format("YYYY-MM-DD");

                                const earlyCloseDate =
                                  this.props.location.earlyCloseTime;
                                const openFromDate =
                                  this.props.location.openFromTime;
                                const timeAllowed =
                                  earlyCloseDate &&
                                  earlyCloseDate[selectedDateTime]
                                    ? Number(earlyCloseDate[selectedDateTime])
                                    : 24;
                                const timeOpenFrom =
                                  openFromDate &&
                                  openFromDate[selectedDateTime]
                                    ? Number(openFromDate[selectedDateTime])
                                    : 0;

                                const [hoursStr, minutesStr] =
                                  key.timeStart.split(":");
                                const hours = parseInt(hoursStr);
                                const minutes = parseInt(minutesStr);
                                const decimalHours = hours + minutes / 60;

                                if (
                                  decimalHours >= timeAllowed ||
                                  decimalHours < timeOpenFrom
                                ) {
                                  hideCount++;
                                  return null;
                                }

                                return (
                                  <TimeSelection
                                    key={i}
                                    objectKey={key.timeStart}
                                    time={key.capacity}
                                    selectedTime={this.state.selectedTime}
                                    select={this.selectTime}
                                    discount={this.props.discount}
                                    interval={
                                      this.props.availibilityInterval
                                        ? this.props.availibilityInterval
                                        : this.state.interval
                                    }
                                    displayInterval={
                                      this.props.displayInterval
                                        ? this.props.displayInterval
                                        : this.state.interval
                                    }
                                  />
                                );
                              });

                            if (
                              hideCount ===
                              this.state.locationAvailability.length
                            ) {
                              return (
                                <p>
                                  Sorry, No times available for the date
                                  selected.
                                </p>
                              );
                            }

                            return visibleTimes;
                          })()
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="modalFooter">
                {this.state.step === 1 ? (
                  <Button
                    className="selectButtonMobile"
                    onClick={() => {
                      this.handleStep(1);
                    }}
                  >
                    Select a time{" "}
                    <span>
                      <img src={Images.whiteRightArrow} alt="" />
                    </span>
                  </Button>
                ) : (
                  <Button
                    className="selectButtonMobile"
                    onClick={() => {
                      this.props.updateSelectedService();
                      this.props.toggleDateModal(false);
                      this.setState({
                        step: 1,
                      });
                    }}
                  >
                    Confirm Date & Time
                  </Button>
                )}
              </div>
            </Box>
          )}
        </Modal>
      </div>
    );
  }
}

export default BookingTimeModal;
